import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Logo } from '@/components/brand/Logo';
import { HeroSection } from '@/components/roi/HeroSection';
import { SelfServiceCalculator } from '@/components/roi/SelfServiceCalculator';
import { ContentiousModule } from '@/components/roi/ContentiousModule';
import { MarketSize } from '@/components/roi/MarketSize';
import { International } from '@/components/roi/International';
import { MethodologyFooter } from '@/components/roi/MethodologyFooter';
import { cn } from '@/lib/utils';

/** Same navy resolve as the pitch page — see PAGE_BG there. */
const PAGE_BG = 'linear-gradient(180deg, #0F172A 0%, #121E3A 100%)';

type TabId = 'roi' | 'market' | 'international';

const TABS: { id: TabId; label: string }[] = [
  { id: 'roi', label: 'Per-firm ROI' },
  { id: 'market', label: 'UK market' },
  { id: 'international', label: 'International' },
];

function parseTabFromHash(hash: string): TabId | null {
  const id = hash.replace(/^#/, '');
  return id === 'roi' || id === 'market' || id === 'international' ? id : null;
}

interface RoiPageProps {
  embedded?: boolean;
}

export function RoiPage({ embedded = false }: RoiPageProps) {
  const [tab, setTab] = useState<TabId>(
    () => parseTabFromHash(window.location.hash) ?? 'roi',
  );

  useEffect(() => {
    const previous = document.title;
    document.title = 'Asset Hound · Investor ROI calculator';
    return () => {
      document.title = previous;
    };
  }, []);

  useEffect(() => {
    const onHashChange = () => {
      const next = parseTabFromHash(window.location.hash);
      if (next) setTab(next);
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  const handleSelect = (next: TabId) => {
    setTab(next);
    if (window.location.hash !== `#${next}`) {
      history.replaceState(null, '', `#${next}`);
    }
  };

  return (
    <div
      className={cn(
        'relative overflow-hidden',
        embedded ? 'min-h-full' : 'min-h-screen',
      )}
      style={{ background: embedded ? undefined : PAGE_BG }}
    >
      {!embedded ? (
        <header className="relative z-20 mx-auto flex max-w-6xl items-center px-8 py-6">
          <Logo size="sm" />
        </header>
      ) : null}

      <main className="relative z-10 flex flex-col gap-12 pb-16 sm:gap-16 sm:pb-20">
        <HeroSection />

        <div className="mx-auto w-full max-w-6xl px-8">
          <TabSwitcher tab={tab} onSelect={handleSelect} />
        </div>

        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={tab}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="flex flex-col gap-12 sm:gap-16"
          >
            {tab === 'roi' ? (
              <RoiTabContent />
            ) : tab === 'market' ? (
              <MarketSize />
            ) : (
              <International />
            )}
          </motion.div>
        </AnimatePresence>
      </main>

      <MethodologyFooter />
    </div>
  );
}

function RoiTabContent() {
  return (
    <>
      <section
        aria-label="Standard insolvency work ROI"
        className="mx-auto w-full max-w-6xl px-8"
      >
        <div className="mb-10 flex flex-col gap-2">
          <span className="text-xs font-medium uppercase tracking-[0.12em] text-ink-dim">
            Standard insolvency work
          </span>
          <h2 className="text-2xl font-semibold leading-tight text-ink-strong sm:text-3xl">
            Model the saving for any firm profile
          </h2>
          <p className="max-w-2xl text-sm leading-relaxed text-ink-muted">
            Start from practitioner-validated assumptions, then adjust case
            volume, charge-out rates, and hours per case. The Asset Hound saving
            percentage stays fixed by profile so the model does not ask viewers
            to guess it
          </p>
        </div>

        <SelfServiceCalculator />
      </section>

      <section className="mx-auto w-full max-w-6xl px-8">
        <ContentiousModule />
      </section>
    </>
  );
}

function TabSwitcher({
  tab,
  onSelect,
}: {
  tab: TabId;
  onSelect: (next: TabId) => void;
}) {
  return (
    <div
      role="tablist"
      aria-label="Investor view"
      className="inline-flex gap-1 rounded-lg border border-border bg-bg-card p-1"
    >
      {TABS.map((t) => {
        const selected = t.id === tab;
        return (
          <button
            key={t.id}
            role="tab"
            type="button"
            aria-selected={selected}
            tabIndex={selected ? 0 : -1}
            onClick={() => onSelect(t.id)}
            className="relative rounded-md px-4 py-2 text-sm font-medium"
          >
            {selected ? (
              <motion.span
                layoutId="active-tab-pill"
                aria-hidden
                className="absolute inset-0 rounded-md bg-bg-nav-active"
                transition={{ type: 'spring', duration: 0.45, bounce: 0.18 }}
              />
            ) : null}
            <span
              className={
                'relative transition-colors ' +
                (selected ? 'text-ink-strong' : 'text-ink-muted')
              }
            >
              {t.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}
